import { Box, Stack } from "@mui/system";
import DatacollectionLastRunTabs from "./DatacollectionLastRunTabs";
import DatacollectionLastRunValueFlow from "./DatacollectionLastRunValueFlow";
import { Chip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { selectDeepViewLoading } from "../../features/deepViewSlice";
import DeepViewHeaderSkeleton from "./DeepViewHeaderSkeleton";
import DeepViewEmptyState from "./DeepViewEmptyState";
import {
  getDeepViewFieldKey,
  getDeepViewLineageEntry,
} from "./deepViewFieldMap";
import ClickOut from "./ClickOut/ClickOut";

interface Props {
  /** Label of the column the user clicked, e.g. "Total Income (USD)" */
  columnLabel?: string;
  /** API key of the column the user clicked, e.g. "total_income_usd" */
  columnKey?: string;
  /** Already-formatted value of the clicked cell, e.g. "$56,873" */
  value?: string | number;
}

const DatacollectionLastRunDetailsView = ({
  columnLabel,
  columnKey,
  value,
}: Props) => {
  const isLoading = useSelector(selectDeepViewLoading);
  const deepViewData = useSelector(
    (state: any) => state?.deepView?.deepViewData,
  );
  const period = deepViewData?.period;
  const lineageEntry = getDeepViewLineageEntry(deepViewData, columnKey);
  const periodLabel =
    period?.fmt_start_date && period?.fmt_end_date
      ? `Period: ${period.fmt_start_date} · ${period.fmt_end_date}`
      : null;

  const isClicks = getDeepViewFieldKey(columnKey) === "clicks";

  // The API can return an empty payload — without a period there is no brand,
  // no date range and nothing for the stages to explain.
  const hasDeepViewData = !!period;

  const chipSx = {
    height: 20,
    borderRadius: "5px",
    fontSize: "12px",
    color: "#00000099",
    borderColor: "#BDBDBD",
    bgcolor: "#FAFAFA",
    "& .MuiChip-label": {
      px: 0.5,
    },
  };

  return (
    <>
      <Box px={2.1} pt={2}>
        <Stack>
          <Typography
            sx={{
              color: "#7B1FA2",
              fontWeight: "500",
              fontSize: "15px",
              textTransform: "uppercase",
            }}
            mb={0.5}
            variant="body1"
          >
            {columnLabel}
          </Typography>
          <Typography
            variant="h6"
            color={"#000000DE"}
            mb={0.5}
            fontSize={"20px"}
            fontWeight={"500"}
          >
            {value}
          </Typography>
        </Stack>

        {isLoading ? (
          <DeepViewHeaderSkeleton />
        ) : (
          hasDeepViewData && (
            <>
              <Typography sx={{ fontSize: "12px" }} mb={0.5} variant="body1">
                {period?.brand_display_name} ({period?.brand_id})
              </Typography>

              <Stack direction="row" spacing={1}>
                {periodLabel && (
                  <Chip label={periodLabel} variant="outlined" sx={chipSx} />
                )}
                {period?.destination_table_name && (
                  <Chip
                    label={`Table: ${period.destination_table_name}`}
                    variant="outlined"
                    sx={chipSx}
                  />
                )}
                {lineageEntry?.field_name && (
                  <Chip
                    label={`Field: ${lineageEntry.field_name}`}
                    variant="outlined"
                    sx={chipSx}
                  />
                )}
              </Stack>

              {!isClicks && (
                <DatacollectionLastRunValueFlow columnKey={columnKey} />
              )}
            </>
          )
        )}
      </Box>

      {!isLoading && !hasDeepViewData ? (
        <DeepViewEmptyState />
      ) : isClicks ? (
        <Box p={2.1}>
          <ClickOut />
        </Box>
      ) : (
        <DatacollectionLastRunTabs
          columnKey={columnKey}
          columnLabel={columnLabel}
        />
      )}
    </>
  );
};

export default DatacollectionLastRunDetailsView;
